import { useState } from 'react';
import styled from 'styled-components';
import { CollapseIcon } from '../../../components/Icons/CollapseIcon';
import { ExpandIcon } from '../../../components/Icons/ExpandIcon';
import { DragIndicator } from '../../CreateVehicle/icons/DragIndicator';
import { useField } from 'react-final-form';
import { GradeField } from '../components/grade';
import { PhotosView } from '../components/photos';
import { CheckboxQuestions } from '../components/checkbox/CheckboxField';
import { NotesIcon } from '../icons/notes';
import { NotesField } from '../components/notes';
import { TiresGradeSelection } from '../components/tires/gradeSelection';
import { SoundRecordingProvider, useSoundRecordingContext } from '../SoundRecorder/context';
import { SoundRecorder } from '../SoundRecorder';
import { OdometerImages } from '../OdometerImages';
import { RecordingPlayer } from '../SoundRecorder/RecordingPlayer';
import { SoundPlayer } from '../components/soundPlayer';
import { useGetVehicleByIdQuery } from 'services/api/upload-vehicle';
import { LicensePlateDate } from '../components/tires/licensePlateDate';
import { useVehicleEditing } from 'context/isAnotherUserEditingContext';
import { NotesTopIcon } from 'components/VehicleCommon/Notes/NotesTopIcon';
import { SyncManager } from 'db';
import { useFormVehicleId } from '../hooks/useFormVehicleId';
import { ReusableNotesField } from '../components/notes/ReusableNotesField';
import { maxLength } from 'utils/validators';

type Props = {
  theme?: 'white' | 'grey';

  forceOpen?: boolean;
  notesOpened?: boolean;
};

const Wrapper = styled.div<{ withBorder?: boolean; background: string; disabled?: boolean }>`
  background: ${props => props.background};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 8px 9px 8px;
  width: 100%;
  outline: ${props => (props.withBorder ? '1px solid #3994de' : 'none')};
  pointer-events: ${props => (props.disabled ? '0.7' : 1)};
`;

const HeaderControls = styled.div<{ disabled: boolean }>`
  display: flex;
  align-items: center;
  margin-left: auto;
  pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
`;

const IconWrapper = styled.div<{ withAutoMargin?: boolean }>`
  display: flex;
  position: relative;
  margin-left: ${props => (props.withAutoMargin ? 'auto' : 0)};
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0px;
`;

const Title = styled.div`
  font-size: 12px;
  font-weight: 600;
  line-height: 14.06px;
  color: #000;
  margin-left: 8px;
`;

const fillColor = 'rgba(153, 153, 153, 1)';

const AutoCheckButton = styled.div`
  background: #3994de;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  line-height: 14.06px;
  color: #fff;
  height: 32px;
  width: 100%;
`;

export const themeOptions = {
  white: {
    background: '#fff',
    gradeBackground: '#2222220A'
  },
  grey: {
    background: '#F5F5F5',
    gradeBackground: '#fff'
  }
};

const maxLengthValidation = maxLength(500);

export const InspectorPrivateNotes = ({ theme = 'grey', forceOpen = false, notesOpened = false }: Props) => {
  const { background } = themeOptions[theme];

  const [error, setError] = useState('');
  const [isNotesFieldOpen, setIsNotesFieldOpen] = useState(forceOpen || notesOpened);

  const toggleNotes = () => {
    setIsNotesFieldOpen(curr => !curr);
  };

  const toggleExpanded = () => toggleNotes();

  const { hasAnotherEditor } = useVehicleEditing();

  const withBorder = theme === 'white';

  const id = useFormVehicleId();

  const {
    input: { onChange: changeValue },
    meta: { error: notesError }
  } = useField(`private_inspector_notes.message`, {
    subscription: { value: true, error: true },
    validate: maxLengthValidation
  });

  const saveComment = async (comment: string | null) => {
    changeValue(comment);
    if (comment && comment.length > 500) {
      setError('Max length is 500 characters');
    } else {
      console.log('save comment in else')
      setError('');
      const syncManager = SyncManager.getInstance(id);
      await syncManager.queueVehicleUpdate(['private_inspector_notes.notes', comment]);
    }
  };

  return (
    <Wrapper withBorder={withBorder} background={background}>
      <HeaderRow>
        <IconWrapper onClick={toggleExpanded}>
          {isNotesFieldOpen ? <CollapseIcon fillColor={fillColor} /> : <ExpandIcon fillColor={fillColor} />}
        </IconWrapper>
        <Title>{'Inspector private comments'}</Title>
        <HeaderControls disabled={hasAnotherEditor}>
          <IconWrapper onClick={toggleNotes}>
            <NotesIcon isActive={isNotesFieldOpen} />
          </IconWrapper>
          <IconWrapper>
            <DragIndicator />
          </IconWrapper>
        </HeaderControls>
      </HeaderRow>
      {isNotesFieldOpen && (
        <ReusableNotesField
          error={error}
          getFieldKey={createInspectorNotesKey}
          save={saveComment}
          autoFocus
          sectionId={`private_inspector_notes`}
        />
      )}
    </Wrapper>
  );
};

const createInspectorNotesKey = (sectionId: string, field?: string) => {
  return [sectionId, field].filter(Boolean).join('.');
};
